import {
	Component,
	OnInit,
	OnDestroy,
	ViewChild,
	ElementRef,
	Inject,
} from '@angular/core';
import { SharedService } from '../shared/shared.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ApiResponse } from '../shared/api.model';
import { UserData } from '../shared/user.model';
import { AccountData } from '../shared/account.model';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatPaginator,
	MatTableDataSource,
} from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
	DETAIL_SECTIONS,
	SUMMARY_STRIP,
	HPDAR_BASIC_SECTIONS,
	HPDAR_SUMMARY_STRIP,
	HPDAR_RESERVED_DETAIL_KEYS,
	HPDAR_STATUS_DEFAULT,
	HPDAR_STATUS_OPTIONS,
	isHpdarAccount,
	isHpdarStatusActive,
	normalizeHpdarStatusValue,
	RESERVED_DETAIL_KEYS,
	ADDITIONAL_FIELD_LABELS,
	DetailFieldSlot,
	DetailRow,
	DetailDateFormat,
	DetailFieldFormat,
	DetailSelectOption,
} from './passenger-details-layout';

const PASSENGER_MAX_LENGTHS = {
	passId: 11,
	firstName: 20,
	lastName: 20,
};

@Component({
	selector: 'app-passengers',
	templateUrl: './passengers.component.html',
	styleUrls: ['./passengers.component.css'],
})
export class PassengersComponent implements OnInit, OnDestroy {
	passengers: any[] = [];
	passengersDataSource: MatTableDataSource<any> = new MatTableDataSource();
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('filtLastName') filtLastName: ElementRef;
	@ViewChild('filtFirstName') filtFirstName: ElementRef;
	@ViewChild('filtPassId') filtPassId: ElementRef;
	sorting = { column: '', direction: 'asc' as 'asc' | 'desc' };
	navigationSubscription: Subscription;
	appUser: UserData;
	currentAccount: AccountData;
	accountIndex: number;
	showSpinner: boolean;
	displayedColumns: string[] = [];
	private passengerRouteKey = '';

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private shared: SharedService,
		private http: HttpClient,
		public dialog: MatDialog
	) {
		this.navigationSubscription = this.router.events.subscribe((e: any) => {
			if (e instanceof NavigationEnd) {
				this.reloadPassengerPage();
			}
		});
	}

	ngOnInit() {
		this.passengerRouteKey = '';
		this.reloadPassengerPage();
	}

	private reloadPassengerPage(): void {
		const qp = this.route.snapshot.queryParams;
		const routeKey =
			(qp['chargeNbr'] || '').toString() +
			'|' +
			(qp['fleetId'] || '').toString() +
			'|' +
			String(this.shared.accountIndex);
		if (routeKey === this.passengerRouteKey) {
			return;
		}
		this.passengerRouteKey = routeKey;
		this.showSpinner = false;
		this.appUser = this.shared.appUser;
		this.syncPassengerAccount();
		this.getPassengers();
	}

	/** Align account selection and API fleetId with charge number (and route/query). */
	private syncPassengerAccount(): void {
		if (!this.appUser || !this.appUser.accounts) {
			return;
		}

		const qp = this.route.snapshot.queryParams;
		const chargeNbr = (
			qp['chargeNbr'] ||
			(this.shared.currentAccount &&
				this.shared.currentAccount.custId) ||
			''
		)
			.toString()
			.trim()
			.toUpperCase();

		if (
			!chargeNbr ||
			this.passengersAccountIds.indexOf(chargeNbr) === -1
		) {
			this.currentAccount = this.shared.currentAccount;
			this.accountIndex = this.shared.accountIndex;
			return;
		}

		const fleetFromQuery = (qp['fleetId'] || '')
			.toString()
			.trim()
			.toUpperCase();
		let index = fleetFromQuery
			? this.shared.findAccountIndexByChargeAndFleet(
					chargeNbr,
					fleetFromQuery
			  )
			: -1;
		if (index < 0) {
			index = this.shared.findAccountIndexByChargeForPassengers(
				chargeNbr
			);
		}
		if (index >= 0) {
			this.accountIndex = index;
			this.currentAccount = this.appUser.accounts[index];
			if (this.shared.accountIndex !== index) {
				this.shared.setAccountIndex(index);
			}
		} else {
			this.currentAccount = this.shared.currentAccount;
			this.accountIndex = this.shared.accountIndex;
		}
	}

	private getPassengerFleetId(): string {
		if (!this.currentAccount) {
			return '';
		}
		return this.shared.getPassengerFleetId(this.currentAccount);
	}

	ngOnDestroy() {
		if (this.navigationSubscription) {
			this.navigationSubscription.unsubscribe();
		}
	}

	private readonly passengersAccountIds = ['1100', 'BGSC', 'HPDAR'];

	/** Accounts that support passenger management (account dropdown). */
	get passengersAccountOptions(): { account: AccountData; index: number }[] {
		if (!this.appUser || !this.appUser.accounts) return [];
		return this.appUser.accounts
			.map((account, index) => ({ account, index }))
			.filter(
				({ account }) =>
					this.passengersAccountIds.indexOf(
						(account.custId || '').toUpperCase()
					) !== -1 && account.onHold === ''
			);
	}

	setIndex(index: number) {
		const account = this.appUser.accounts[index];
		this.accountIndex = index;
		this.currentAccount = account;
		this.shared.setAccountIndex(index);
		this.passengerRouteKey = '';
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: {
				chargeNbr: account.custId,
				fleetId: this.shared.getPassengerFleetId(account),
			},
			queryParamsHandling: 'merge',
		});
		this.shared.buildBreadCrumbs(
			window.location.pathname.substring(1),
			this.appUser
		);
		this.getPassengers();
	}

	getPassengers(showLoader: boolean = true) {
		if (!this.shared.appInit || !this.currentAccount || !this.shared.appUser || !this.shared.appUser.identity) {
			this.showSpinner = false;
			return;
		}
		if (showLoader) {
			this.showSpinner = true;
		}
		const params = this.buildPassengerRequestParams('23');

		this.http
			.get(this.shared.appInit.apiPath + 'getpassengers', {
				params,
				withCredentials: false,
			})
			.subscribe(
				(response: ApiResponse) => {
					this.showSpinner = false;
					if (response.errorMessage) {
						this.shared.openMessageDialog(
							'Data Access Error',
							response.errorMessage
						);
						return;
					}
					const data = JSON.parse(response.body);
					if (data.status === 'OK') {
						this.passengers = data.passengers || [];
						this.buildDisplayColumns();
						this.applyFilter();
						setTimeout(() => {
							this.passengersDataSource.paginator = this.paginator;
						}, 0);
					} else if (data.status === 'AUTHENTICATION_ERROR') {
						this.shared.setAuthError();
						this.router.navigate(['/']);
					} else {
						this.shared.openMessageDialog(
							'Error',
							data.error || 'Failed to load passengers.'
						);
					}
				},
				(error) => {
					this.showSpinner = false;
					this.shared.openMessageDialog(
						'Data Access Error',
						'Something went wrong. Please try again.'
					);
				}
			);
	}

	/** action, chargeNbr, fleetId, zone, token, userId, application — required for passenger APIs. */
	private buildPassengerRequestParams(action: string): HttpParams {
		const chargeNbr = (this.currentAccount.custId || '')
			.toString()
			.trim()
			.toUpperCase();
		const fleetId = this.getPassengerFleetId();
		return new HttpParams()
			.append('action', action)
			.append('chargeNbr', chargeNbr)
			.append('fleetId', fleetId)
			.append('zone', this.shared.appUser.zoneId)
			.append('token', this.shared.appUser.identity.Token)
			.append('userId', this.shared.appUser.userId)
			.append(
				'application',
				this.shared.appInit.application.toString()
			);
	}

	onAddPassenger(): void {
		if (!this.currentAccount || !this.shared.appUser) {
			return;
		}
		const dialogRef = this.dialog.open(AddPassengerDialogComponent, {
			width: '480px',
			maxWidth: '95vw',
			data: {
				account: this.currentAccount,
				appUser: this.shared.appUser,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === 'created') {
				this.getPassengers();
			}
		});
	}

	private buildDisplayColumns() {
		if (this.passengers.length === 0) {
			this.displayedColumns = [];
			this.passengersDataSource.data = [];
		} else {
			this.displayedColumns = [
				'rowNumber',
				'pass_id',
				'pass_first_name',
				'pass_last_name',
				'actions',
			];
		}
	}

	onFilter() {
		this.applyFilter();
		if (this.passengersDataSource.paginator) {
			this.passengersDataSource.paginator.firstPage();
		}
	}

	private applyFilter() {
		const last = this.filtLastName && this.filtLastName.nativeElement
			? (this.filtLastName.nativeElement.value || '').trim().toLowerCase()
			: '';
		const first = this.filtFirstName && this.filtFirstName.nativeElement
			? (this.filtFirstName.nativeElement.value || '').trim().toLowerCase()
			: '';
		const passId = this.filtPassId && this.filtPassId.nativeElement
			? (this.filtPassId.nativeElement.value || '').trim().toLowerCase()
			: '';

		if (!last && !first && !passId) {
			this.passengersDataSource.data = this.passengers.slice();
			return;
		}
		const filtered = this.passengers.filter((p: any) => {
			const ln = (p.pass_last_name != null ? String(p.pass_last_name) : '').toLowerCase();
			const fn = (p.pass_first_name != null ? String(p.pass_first_name) : '').toLowerCase();
			const id = (p.pass_id != null ? String(p.pass_id) : '').toLowerCase();
			const matchLast = !last || ln.indexOf(last) !== -1;
			const matchFirst = !first || fn.indexOf(first) !== -1;
			const matchId = !passId || id.indexOf(passId) !== -1;
			return matchLast && matchFirst && matchId;
		});
		this.passengersDataSource.data = filtered;
	}

	sortData(column: string) {
		if (this.sorting.column === column) {
			this.sorting.direction = this.sorting.direction === 'asc' ? 'desc' : 'asc';
		} else {
			this.sorting.column = column;
			this.sorting.direction = 'asc';
		}
		const data = this.passengersDataSource.data.slice();
		const dir = this.sorting.direction === 'asc' ? 1 : -1;
		data.sort((a, b) => {
			const va = a[column] != null ? a[column] : '';
			const vb = b[column] != null ? b[column] : '';
			if (va < vb) return -1 * dir;
			if (va > vb) return 1 * dir;
			return 0;
		});
		this.passengersDataSource.data = data;
	}

	formatColumnName(col: string): string {
		const labels: { [key: string]: string } = {
			pass_id: 'Pass ID',
			pelg_pass_id: 'Eligibility Pass ID',
			pass_first_name: 'First Name',
			pass_last_name: 'Last Name',
			pass_name: 'Name',
			pelg_first_name: 'First Name',
			pelg_last_name: 'Last Name',
			pass_phone: 'Phone',
			pass_phone_nbr: 'Phone',
			pelg_phone: 'Phone',
			pass_email: 'Email',
			pelg_email: 'Email',
			pelg_cu_charge_nbr: 'Account',
			pelg_exts_site: 'Site',
		};
		return labels[col] || col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
	}

	getValue(passenger: any, col: string): any {
		const v = passenger[col];
		if (v == null) return '';
		return v;
	}

	openPassengerDetails(passenger: any): void {
		if (
			!passenger ||
			!passenger.pass_id ||
			!this.shared.appInit ||
			!this.currentAccount ||
			!this.shared.appUser ||
			!this.shared.appUser.identity
		) {
			return;
		}
		this.showSpinner = true;
		let params = this.buildPassengerRequestParams('26').append(
			'pass_id',
			String(passenger.pass_id).trim()
		);

		this.http
			.get(this.shared.appInit.apiPath + 'getpassengers', {
				params,
				withCredentials: false,
			})
			.subscribe(
				(response: ApiResponse) => {
					this.showSpinner = false;
					if (response.errorMessage) {
						this.shared.openMessageDialog(
							'Data Access Error',
							response.errorMessage
						);
						return;
					}
					const data = JSON.parse(response.body);
					if (data.status === 'OK' && data.passenger) {
						const dialogRef = this.dialog.open(
							PassengerDetailsDialogComponent,
							{
								width: '100vw',
								maxWidth: '100vw',
								height: '100vh',
								maxHeight: '100vh',
								data: {
									passenger: data.passenger,
									account: this.currentAccount,
									appUser: this.shared.appUser,
								},
								panelClass: [
									'mat-elevation-z5',
									'passenger-details-fullscreen',
								],
							}
						);

						dialogRef.afterClosed().subscribe((result) => {
							if (result && result.type === 'save' && result.payload) {
								this.savePassengerDetails(result.payload);
							} else if (result && result.type === 'deleted') {
								this.getPassengers(false);
								this.shared.openToastMessage(
									'Passenger removed successfully.',
									false
								);
							}
						});
					} else if (data.status === 'AUTHENTICATION_ERROR') {
						this.shared.setAuthError();
						this.router.navigate(['/']);
					} else if (data.status === 'NOT_FOUND') {
						this.shared.openMessageDialog(
							'Passenger Not Found',
							'Unable to load this passenger. It may have been removed.'
						);
					} else {
						this.shared.openMessageDialog(
							'Error',
							data.error || 'Failed to load passenger details.'
						);
					}
				},
				() => {
					this.showSpinner = false;
					this.shared.openMessageDialog(
						'Data Access Error',
						'Something went wrong. Please try again.'
					);
				}
			);
	}

	private savePassengerDetails(payload: any): void {
		if (
			!payload ||
			!this.shared.appInit ||
			!this.currentAccount ||
			!this.shared.appUser ||
			!this.shared.appUser.identity
		) {
			this.shared.openToastMessage(
				'Failed to save passenger details. Please try again.',
				true
			);
			return;
		}

		let params = this.buildPassengerRequestParams('25');
		Object.keys(payload).forEach((key) => {
			const rawValue = payload[key];
			if (rawValue === undefined || rawValue === null) {
				return;
			}
			params = params.append(key, String(rawValue));
		});

		this.http
			.post(this.shared.appInit.apiPath + 'getpassengers', {}, {
				params,
				withCredentials: false,
			})
			.subscribe(
				(response: ApiResponse) => {
					if (response.errorMessage) {
						this.shared.openToastMessage(response.errorMessage, true);
						return;
					}
					const data = JSON.parse(response.body);
					if (data.status === 'OK') {
						this.getPassengers(false);
						this.shared.openToastMessage(
							'Passenger Details Saved successfully',
							false
						);
					} else if (data.status === 'AUTHENTICATION_ERROR') {
						this.shared.setAuthError();
						this.router.navigate(['/']);
					} else {
						this.shared.openToastMessage(
							data.error || 'Failed to save passenger details.',
							true
						);
					}
				},
				() => {
					this.shared.openToastMessage(
						'Failed to save passenger details. Please try again.',
						true
					);
				}
			);
	}
}

@Component({
	selector: 'app-add-passenger-dialog',
	templateUrl: './passenger-add-dialog.html',
	styleUrls: ['./passengers.component.css'],
})
export class AddPassengerDialogComponent implements OnInit {
	form: FormGroup;
	showSpinner = false;

	constructor(
		public dialogRef: MatDialogRef<AddPassengerDialogComponent>,
		private fb: FormBuilder,
		private http: HttpClient,
		private router: Router,
		private shared: SharedService,
		@Inject(MAT_DIALOG_DATA) public data: { account: AccountData; appUser: UserData }
	) {}

	ngOnInit(): void {
		this.form = this.fb.group({
			passId: [
				'',
				[
					Validators.required,
					Validators.pattern(/^[A-Za-z0-9]+$/),
					Validators.maxLength(PASSENGER_MAX_LENGTHS.passId),
				],
			],
			firstName: [
				'',
				[
					Validators.required,
					Validators.maxLength(PASSENGER_MAX_LENGTHS.firstName),
				],
			],
			lastName: [
				'',
				[
					Validators.required,
					Validators.maxLength(PASSENGER_MAX_LENGTHS.lastName),
				],
			],
		});
	}

	get passId() {
		return this.form.get('passId');
	}
	get firstName() {
		return this.form.get('firstName');
	}
	get lastName() {
		return this.form.get('lastName');
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.form.invalid || !this.shared.appInit || !this.data || !this.data.account || !this.data.appUser) {
			return;
		}

		const appUser = this.data.appUser;
		const account = this.data.account;
		const passIdValue = String(this.passId.value || '').trim();
		const firstNameValue = String(this.firstName.value || '').trim();
		const lastNameValue = String(this.lastName.value || '').trim();
		if (!passIdValue || !firstNameValue || !lastNameValue) {
			this.passId.setValue(passIdValue);
			this.firstName.setValue(firstNameValue);
			this.lastName.setValue(lastNameValue);
			this.passId.markAsTouched();
			this.firstName.markAsTouched();
			this.lastName.markAsTouched();
			return;
		}

		this.showSpinner = true;
		const chargeNbr = (account.custId || '').toString().trim().toUpperCase();
		const fleetId = this.shared.getPassengerFleetId(account);
		let params = new HttpParams()
			.append('action', '24')
			.append('chargeNbr', chargeNbr)
			.append('fleetId', fleetId)
			.append('zone', appUser.zoneId)
			.append('token', appUser.identity.Token)
			.append('userId', appUser.userId)
			.append('application', this.shared.appInit.application.toString())
			.append('pass_id', passIdValue)
			.append('pass_first_name', firstNameValue)
			.append('pass_last_name', lastNameValue);

		this.http
			.post(this.shared.appInit.apiPath + 'getpassengers', {}, {
				params,
				withCredentials: false,
			})
			.subscribe(
				(response: ApiResponse) => {
					this.showSpinner = false;
					if (response.errorMessage) {
						this.shared.openMessageDialog('Data Access Error', response.errorMessage);
						return;
					}
					const data = JSON.parse(response.body);
					if (data.status === 'OK') {
						this.dialogRef.close('created');
					} else if (data.status === 'AUTHENTICATION_ERROR') {
						this.shared.setAuthError();
						this.router.navigate(['/']);
						this.dialogRef.close();
					} else {
						const msg =
							data.error === 'Passenger already exists.' ?
							'Passenger already exists.' :
							(data.error || 'Failed to create passenger.');
						this.shared.openMessageDialog('Error', msg);
					}
				},
				() => {
					this.showSpinner = false;
					this.shared.openMessageDialog(
						'Data Access Error',
						'Something went wrong. Please try again.'
					);
				}
			);
	}
}

interface ResolvedSlot {
	label: string;
	control: 'text' | 'checkbox' | 'textarea' | 'select';
	key?: string;
	text?: string;
	checked?: boolean;
	options?: DetailSelectOption[];
	editable?: boolean;
}

interface ResolvedRow {
	slots: ResolvedSlot[];
}

interface ResolvedSectionBlock {
	section: (typeof DETAIL_SECTIONS)[number];
	rows: ResolvedRow[];
}

interface HpdarFieldGroup {
	id: string;
	title: string;
	icon: string;
	items: ResolvedSlot[];
}

const HPDAR_IDENTITY_LABELS = new Set([
	'Passenger ID',
	'First Name',
	'Middle Name',
	'Last Name',
]);
const HPDAR_IDENTITY_FIELD_ORDER = [
	'Passenger ID',
	'First Name',
	'Middle Name',
	'Last Name',
];
const HPDAR_CONTACT_LABELS = new Set(['Phone Number']);
const HPDAR_ADDRESS_LABELS = new Set([
	'Street Number',
	'Street Name',
	'Apt / Room',
	'City',
	'State',
	'ZIP Code',
]);

const READ_ONLY_PASSENGER_FIELDS = new Set<string>([
	'pass_id',
	'pass_home_loc_id',
	'pass_exts_site',
	'pass_permanent',
	'pass_def_priority',
	'pelg_rec_nbr',
	'pelg_pass_id',
	'pelg_cu_charge_nbr',
	'pelg_exts_site',
	'pelg_pcap_max',
	'loc_id',
	'loc_charge_nbr',
	'loc_exts_site',
	'loc_fl_id',
	'loc_last_used_dt_tm',
	'loc_permanent',
	'loc_priority',
]);

const NUMERIC_PASSENGER_FIELDS = new Set<string>([
	'pass_nbr_escorts',
	'loc_str_nbr',
	'loc_lon',
	'loc_lat',
	'loc_zn_nbr',
	'loc_ph_type',
	'loc_sms_provider',
	'loc_owes_dr_id',
	'loc_owes_dr_amt',
	'loc_vh_attr_rule',
	'loc_dr_attr_rule',
	'loc_bttn_broadcast_only',
	'loc_guar_rate_perc_disc_dest',
	'loc_guar_rate_perc_disc_pkup',
	'pelg_pass_allowed_trips_per_month',
	'pelg_pass_allowed_miles_per_month',
	'pelg_pass_preferred_prov_nbr',
	'pelg_pass_allowed_amount_per_month',
	'pelg_pass_unit_amt',
	'pelg_pass_allowed_units',
]);

const MAX_TEXT_LENGTH_BY_FIELD: { [key: string]: number } = {
	pass_id: 11,
	pass_first_name: 20,
	pass_last_name: 20,
	pass_home_loc_id: 11,
	pass_exts_site: 10,
	pelg_pass_id: 11,
	pelg_cu_charge_nbr: 10,
	pelg_exts_site: 10,
	loc_id: 11,
	loc_name: 30,
	loc_adr_cmnt: 255,
	loc_str_name: 40,
	loc_str_apt_room: 5,
	loc_city: 3,
	loc_state: 2,
	loc_ph_nbr: 10,
	loc_zip_code: 5,
	loc_charge_nbr: 10,
	loc_exts_site: 10,
	loc_fl_id: 3,
	loc_passenger_name: 40,
};

const ELIGIBILITY_PROGRAM_FIELDS: string[] = [
	'pelg_pass_allowed_trips_per_month',
	'pelg_pass_allowed_miles_per_month',
	'pelg_pass_allowed_amount_per_month',
	'pelg_pass_unit_amt',
	'pelg_pass_allowed_units',
];

@Component({
	selector: 'app-passenger-details-dialog',
	templateUrl: './passenger-details-dialog.html',
	styleUrls: ['./passengers.component.css'],
})
export class PassengerDetailsDialogComponent implements OnInit {
	passenger: any = {};
	originalPassenger: any = {};
	account: AccountData;
	appUser: UserData;
	passengerFullName = '';
	resolvedSections: ResolvedSectionBlock[] = [];
	pendingUpdates: { [key: string]: any } = {};
	validationErrors: { [key: string]: string } = {};
	showSpinner = false;
	summaryStrip: {
		record: string;
		passId: string;
		charge: string;
		status: string;
		statusActive: boolean;
	} = {
		record: '',
		passId: '',
		charge: '',
		status: '',
		statusActive: false,
	};
	additionalFields: { key: string; label: string; value: string }[] = [];
	isHpdarBasicView = false;
	hpdarGroups: HpdarFieldGroup[] = [];
	hpdarAddressSlots: ResolvedSlot[] = [];
	hpdarAddressLine = '';
	hpdarAddressEditable = false;
	hpdarStatusSlot: ResolvedSlot | null = null;
	hpdarStatusOptions = HPDAR_STATUS_OPTIONS;

	get isTotallyEmpty(): boolean {
		return !this.passenger || Object.keys(this.passenger).length === 0;
	}

	get hasPendingChanges(): boolean {
		return Object.keys(this.pendingUpdates).length > 0;
	}

	get hasValidationErrors(): boolean {
		return Object.keys(this.validationErrors).length > 0;
	}

	get hpdarStatusValue(): string {
		return this.hpdarStatusSlot && this.hpdarStatusSlot.text
			? this.hpdarStatusSlot.text
			: HPDAR_STATUS_DEFAULT;
	}

	get hpdarStatusIsActive(): boolean {
		return isHpdarStatusActive(this.hpdarStatusValue);
	}

	constructor(
		public dialogRef: MatDialogRef<PassengerDetailsDialogComponent>,
		private http: HttpClient,
		private router: Router,
		private shared: SharedService,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {}

	ngOnInit() {
		this.passenger = this.data && this.data.passenger ? this.data.passenger : {};
		this.originalPassenger = Object.assign({}, this.passenger);
		this.validationErrors = {};
		this.account = this.data && this.data.account ? this.data.account : null;
		this.appUser = this.data && this.data.appUser ? this.data.appUser : null;
		this.isHpdarBasicView = isHpdarAccount(
			this.account && this.account.custId
		);
		this.passengerFullName = this.buildPassengerFullName();
		const detailSections = this.isHpdarBasicView
			? HPDAR_BASIC_SECTIONS
			: DETAIL_SECTIONS;
		this.resolvedSections = detailSections.map((section) => ({
			section,
			rows: section.rows.map((row) => this.normalizeRow(row)),
		}));

		const summaryConfig = this.isHpdarBasicView
			? HPDAR_SUMMARY_STRIP
			: SUMMARY_STRIP;
		const statusRaw = this.isHpdarBasicView
			? this.passenger.pelg_status
			: this.firstMatchingValue(summaryConfig.status);
		const statusStr = this.isHpdarBasicView
			? normalizeHpdarStatusValue(statusRaw)
			: this.formatDisplay(statusRaw);
		this.summaryStrip = {
			record: this.isHpdarBasicView
				? ''
				: this.formatDisplay(
						this.firstMatchingValue(SUMMARY_STRIP.recordNumber)
				  ),
			passId: this.formatDisplay(
				this.firstMatchingValue(summaryConfig.passId)
			),
			charge: this.isHpdarBasicView
				? ''
				: this.formatDisplay(
						this.firstMatchingValue(SUMMARY_STRIP.chargeNumber)
				  ),
			status: statusStr,
			statusActive: this.isHpdarBasicView
				? isHpdarStatusActive(statusRaw)
				: this.isActiveStatus(
						statusRaw !== undefined ? statusRaw : statusStr
				  ),
		};

		const reservedKeys = this.isHpdarBasicView
			? HPDAR_RESERVED_DETAIL_KEYS
			: RESERVED_DETAIL_KEYS;
		this.additionalFields = Object.keys(this.passenger)
			.filter((k) => !reservedKeys.has(k))
			.filter((k) => this.isPresent(this.passenger[k]))
			.sort()
			.map((k) => ({
				key: k,
				label: ADDITIONAL_FIELD_LABELS[k] || this.labelForExtraKey(k),
				value: this.formatExtraFieldValue(k, this.passenger[k]),
			}));

		if (this.isHpdarBasicView) {
			this.initHpdarStatusSlot();
			this.buildHpdarView();
		}
	}

	onHpdarStatusChanged(value: string): void {
		if (!this.hpdarStatusSlot) {
			return;
		}
		const normalized = normalizeHpdarStatusValue(value);
		this.hpdarStatusSlot.text = normalized;
		this.syncHpdarSummaryStatus();
		this.trackHpdarStatusUpdate(normalized);
	}

	showHpdarSlot(slot: ResolvedSlot): boolean {
		if (!slot) {
			return false;
		}
		if (slot.label === 'Passenger ID') {
			return !!(
				(slot.text || '').trim() ||
				(this.summaryStrip.passId || '').trim()
			);
		}
		if (slot.editable) {
			return true;
		}
		const text = (slot.text || '').trim();
		if (!text) {
			return false;
		}
		return true;
	}

	hpdarSlotDisplay(slot: ResolvedSlot): string {
		if (slot.label === 'Passenger ID') {
			return (slot.text || this.summaryStrip.passId || '').trim();
		}
		const text = (slot.text || '').trim();
		if (!text) {
			return '';
		}
		if (
			slot.label === 'First Name' ||
			slot.label === 'Middle Name' ||
			slot.label === 'Last Name'
		) {
			return this.toTitleCaseWords(text);
		}
		return text;
	}

	hpdarInputPlaceholder(slot: ResolvedSlot): string {
		return slot && slot.label ? slot.label : '';
	}

	private initHpdarStatusSlot(): void {
		const normalized = normalizeHpdarStatusValue(this.passenger.pelg_status);
		const options = HPDAR_STATUS_OPTIONS.slice();
		if (
			normalized &&
			!options.some((o) => o.value.toLowerCase() === normalized.toLowerCase())
		) {
			options.push({ label: normalized, value: normalized });
		}
		this.hpdarStatusSlot = {
			label: 'Status',
			control: 'select',
			key: 'pelg_status',
			text: normalized,
			options,
			editable: true,
		};
		this.syncHpdarSummaryStatus();
	}

	private syncHpdarSummaryStatus(): void {
		const text = this.hpdarStatusValue;
		this.summaryStrip.status = text;
		this.summaryStrip.statusActive = isHpdarStatusActive(text);
	}

	private trackHpdarStatusUpdate(value: string): void {
		const key = 'pelg_status';
		const normalized = normalizeHpdarStatusValue(value);
		const original = normalizeHpdarStatusValue(this.originalPassenger[key]);
		delete this.validationErrors[key];
		if (this.isSameValue(normalized, original)) {
			delete this.pendingUpdates[key];
			return;
		}
		this.pendingUpdates[key] = normalized;
	}

	private buildHpdarView(): void {
		const slots =
			this.resolvedSections[0] &&
			this.resolvedSections[0].rows
				? this.resolvedSections[0].rows.reduce(
						(acc: ResolvedSlot[], row) => acc.concat(row.slots),
						[]
				  )
				: [];
		const identity: ResolvedSlot[] = [];
		const contact: ResolvedSlot[] = [];
		this.hpdarAddressSlots = [];

		slots.forEach((slot) => {
			if (HPDAR_IDENTITY_LABELS.has(slot.label)) {
				identity.push(slot);
			} else if (HPDAR_CONTACT_LABELS.has(slot.label)) {
				contact.push(slot);
			} else if (HPDAR_ADDRESS_LABELS.has(slot.label)) {
				this.hpdarAddressSlots.push(slot);
			}
		});

		this.hpdarAddressLine = this.buildHpdarAddressLine();
		this.hpdarAddressEditable = this.hpdarAddressSlots.some((s) => s.editable);

		identity.sort(
			(a, b) =>
				HPDAR_IDENTITY_FIELD_ORDER.indexOf(a.label) -
				HPDAR_IDENTITY_FIELD_ORDER.indexOf(b.label)
		);

		const groups: HpdarFieldGroup[] = [
			{ id: 'identity', title: 'Passenger', icon: 'person', items: identity },
			{ id: 'contact', title: 'Contact', icon: 'phone', items: contact },
		];
		if (
			this.hpdarAddressLine ||
			this.hpdarAddressEditable ||
			this.hpdarAddressSlots.length
		) {
			groups.push({
				id: 'address',
				title: 'Address',
				icon: 'place',
				items: [],
			});
		}
		this.hpdarGroups = groups.filter(
			(g) =>
				g.id === 'identity' ||
				g.id === 'address' ||
				g.items.some((s) => this.showHpdarSlot(s))
		);
	}

	private buildHpdarAddressLine(): string {
		const nbr = this.formatDisplay(this.passenger.loc_str_nbr);
		const name = this.formatDisplay(this.passenger.loc_str_name);
		const apt = this.formatDisplay(this.passenger.loc_str_apt_room);
		const city = this.formatDisplay(this.passenger.loc_city);
		const state = this.formatDisplay(this.passenger.loc_state);
		const zip = this.formatDisplay(this.passenger.loc_zip_code);

		const streetParts = [nbr, name].filter((p) => !!p);
		const street = streetParts.join(' ');
		const parts: string[] = [];
		if (street) {
			parts.push(apt ? `${street}, ${apt}` : street);
		} else if (apt) {
			parts.push(`Apt ${apt}`);
		}

		const locality = [city, state].filter((p) => !!p).join(', ');
		const zipPart = zip ? (locality ? `${locality} ${zip}` : zip) : locality;
		if (zipPart) {
			parts.push(zipPart);
		}
		return parts.join(', ');
	}

	private toTitleCaseWords(value: string): string {
		return value
			.toLowerCase()
			.replace(/\b[a-z]/g, (c) => c.toUpperCase());
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	onDeletePassenger(): void {
		if (
			this.showSpinner ||
			!this.shared.appInit ||
			!this.account ||
			!this.appUser ||
			!this.appUser.identity
		) {
			return;
		}

		const passId =
			this.getFirstPresentValue(['pass_id', 'pelg_pass_id', 'loc_id']) || '';
		if (!passId) {
			this.shared.openMessageDialog(
				'Error',
				'Unable to delete program because pass ID is missing.'
			);
			return;
		}

		const chargeNbr = (this.account.custId || '').toString().trim().toUpperCase();
		const confirmDialog = this.shared.openConfirmDialog(
			'Delete Program',
			`Remove this pass eligibility program for ${passId} on account ${chargeNbr}? This does not delete the passenger record.`
		);

		confirmDialog.componentInstance.Choice.subscribe((confirm: boolean) => {
			if (!confirm) {
				return;
			}

			this.showSpinner = true;
			const fleetId = this.shared.getPassengerFleetId(this.account);
			if (!fleetId) {
				this.showSpinner = false;
				this.shared.openMessageDialog(
					'Configuration Error',
					'Unable to delete program because account fleet is missing.'
				);
				return;
			}
			const params = new HttpParams()
				.append('action', '27')
				.append('chargeNbr', chargeNbr)
				.append('fleetId', fleetId)
				.append('zone', this.appUser.zoneId)
				.append('token', this.appUser.identity.Token)
				.append('userId', this.appUser.userId)
				.append(
					'application',
					this.shared.appInit.application.toString()
				)
				.append('pass_id', String(passId).trim());

			this.http
				.get(this.shared.appInit.apiPath + 'getpassengers', {
					params,
					withCredentials: false,
				})
				.subscribe(
					(response: ApiResponse) => {
						this.showSpinner = false;
						if (!response) {
							this.shared.openMessageDialog(
								'Data Access Error',
								'No response was returned by the server.'
							);
							return;
						}
						if (response.errorMessage) {
							this.shared.openMessageDialog(
								'Data Access Error',
								response.errorMessage
							);
							return;
						}
						if (!response.body) {
							this.shared.openMessageDialog(
								'Data Access Error',
								'Server response was empty.'
							);
							return;
						}
						let data: any;
						try {
							data = JSON.parse(response.body);
						} catch (_e) {
							this.shared.openMessageDialog(
								'Data Access Error',
								'Unable to read server response. Please try again.'
							);
							return;
						}
						if (data.status === 'OK') {
							this.dialogRef.close({ type: 'deleted' });
						} else if (data.status === 'AUTHENTICATION_ERROR') {
							this.shared.setAuthError();
							this.router.navigate(['/']);
							this.dialogRef.close();
						} else if (data.status === 'NOT_FOUND') {
							this.shared.openMessageDialog(
								'Program Not Found',
								data.error || 'Unable to find this program record.'
							);
						} else {
							this.shared.openMessageDialog(
								'Error',
								data.error || 'Failed to delete program.'
							);
						}
					},
					() => {
						this.showSpinner = false;
						this.shared.openMessageDialog(
							'Data Access Error',
							'Something went wrong. Please try again.'
						);
					}
				);
		});
	}

	onTextChanged(slot: ResolvedSlot, value: string): void {
		slot.text = value;
		this.trackSlotUpdate(slot, value);
	}

	onSelectChanged(slot: ResolvedSlot, value: string): void {
		slot.text = value;
		this.trackSlotUpdate(slot, value);
	}

	onCheckboxChanged(slot: ResolvedSlot, checked: boolean): void {
		slot.checked = checked;
		this.trackSlotUpdate(slot, checked);
	}

	onSaveChanges(): void {
		if (
			!this.hasPendingChanges ||
			this.hasValidationErrors ||
			this.showSpinner
		) {
			return;
		}

		const passId =
			this.getFirstPresentValue(['pass_id', 'pelg_pass_id', 'loc_id']) || '';
		if (!passId) {
			this.shared.openMessageDialog(
				'Error',
				'Unable to update passenger because pass ID is missing.'
			);
			return;
		}

		const programError = this.getEligibilityProgramValidationError();
		if (programError) {
			this.shared.openMessageDialog('Validation Error', programError);
			return;
		}

		const payload: any = {
			pass_id: String(passId),
			loc_id: String(this.getFirstPresentValue(['loc_id']) || passId),
			pelg_pass_id: String(this.getFirstPresentValue(['pelg_pass_id']) || passId),
		};
		Object.keys(this.pendingUpdates).forEach((key) => {
			const raw = this.pendingUpdates[key];
			if (raw === undefined || raw === null) {
				return;
			}
			payload[key] = raw;
		});

		this.dialogRef.close({
			type: 'save',
			payload,
		});
	}

	private normalizeRow(row: DetailRow): ResolvedRow {
		const slots = (Array.isArray(row) ? row : [row]).map((s) => this.resolveSlot(s));
		return { slots };
	}

	private resolveSlot(slot: DetailFieldSlot): ResolvedSlot {
		const control = slot.control || 'text';
		const primaryKey = slot.keys && slot.keys.length ? slot.keys[0] : undefined;
		const editable = this.isSlotEditable(slot, primaryKey);
		if (control === 'checkbox') {
			for (const k of slot.keys) {
				if (!Object.prototype.hasOwnProperty.call(this.passenger, k)) {
					continue;
				}
				const v = this.passenger[k];
				if (this.isPresent(v)) {
					return {
						label: slot.label,
						control: 'checkbox',
						key: k,
						checked: this.coerceBool(v),
						editable,
					};
				}
			}
			return {
				label: slot.label,
				control: 'checkbox',
				key: primaryKey,
				checked: false,
				editable,
			};
		}
		if (control === 'select') {
			const rawValue = this.firstMatchingValue(slot.keys);
			const normalized = this.resolveSelectValue(rawValue, slot.selectOptions || [], slot.selectPlaceholder);
			return {
				label: slot.label,
				control: 'select',
				key: primaryKey,
				text: normalized.value,
				options: normalized.options,
				editable,
			};
		}
		for (const k of slot.keys) {
			if (!Object.prototype.hasOwnProperty.call(this.passenger, k)) {
				continue;
			}
			const v = this.passenger[k];
			if (this.isPresent(v)) {
				return {
					label: slot.label,
					control,
					key: k,
					editable,
					text: this.formatFieldValue(v, slot.dateFormat, slot.format),
				};
			}
		}
		return {
			label: slot.label,
			control,
			key: primaryKey,
			editable,
			text: '',
		};
	}

	private resolveSelectValue(
		rawValue: any,
		options: DetailSelectOption[],
		placeholder?: string
	): { value: string; options: DetailSelectOption[] } {
		const baseOptions = options.slice();
		const value = this.formatDisplay(rawValue);
		if (!value) {
			const fallback = placeholder || '';
			if (fallback && !baseOptions.some((o) => o.value === fallback)) {
				baseOptions.unshift({ label: fallback, value: fallback });
			}
			return { value: fallback, options: baseOptions };
		}
		const match = baseOptions.find((o) => o.value.toLowerCase() === value.toLowerCase());
		if (match) {
			return { value: match.value, options: baseOptions };
		}
		return {
			value,
			options: [{ label: value, value }].concat(baseOptions),
		};
	}

	private firstMatchingValue(keys: string[]): any {
		for (const k of keys) {
			if (!Object.prototype.hasOwnProperty.call(this.passenger, k)) {
				continue;
			}
			const v = this.passenger[k];
			if (this.isPresent(v)) {
				return v;
			}
		}
		return undefined;
	}

	private getFirstPresentValue(keys: string[]): any {
		for (const k of keys) {
			if (!Object.prototype.hasOwnProperty.call(this.passenger, k)) {
				continue;
			}
			const v = this.passenger[k];
			if (v !== undefined && v !== null && String(v).trim() !== '') {
				return v;
			}
		}
		return undefined;
	}

	private isSlotEditable(slot: DetailFieldSlot, primaryKey?: string): boolean {
		if (slot.editable === false) {
			return false;
		}
		if (!primaryKey) {
			return false;
		}
		if (READ_ONLY_PASSENGER_FIELDS.has(primaryKey)) {
			return false;
		}
		return true;
	}

	private trackSlotUpdate(slot: ResolvedSlot, rawValue: any): void {
		if (!slot || !slot.key || !slot.editable) {
			return;
		}
		const key = slot.key;
		const validationError = this.validateSlotValue(slot, rawValue);
		if (validationError) {
			this.validationErrors[key] = validationError;
			delete this.pendingUpdates[key];
			return;
		}
		delete this.validationErrors[key];
		const normalized = this.normalizeOutboundValue(key, rawValue);
		const original = this.normalizeOutboundValue(key, this.originalPassenger[key]);

		if (this.isSameValue(normalized, original)) {
			delete this.pendingUpdates[key];
			return;
		}
		this.pendingUpdates[key] = normalized;
	}

	private normalizeOutboundValue(key: string, value: any): any {
		if (value === undefined || value === null) {
			return '';
		}
		if (typeof value === 'boolean') {
			const original = this.originalPassenger[key];
			if (typeof original === 'number') {
				return value ? 1 : 0;
			}
			return value ? 'Y' : 'N';
		}
		let normalizedValue: any = value;
		if (typeof normalizedValue === 'string') {
			normalizedValue = normalizedValue.trim();
		}
		if (key === 'loc_ph_nbr') {
			return String(normalizedValue).replace(/\D/g, '');
		}
		if (key === 'pelg_from_dt_tm' || key === 'pelg_to_dt_tm') {
			const asDate = this.parseToDate(normalizedValue);
			return asDate ? asDate.toISOString() : normalizedValue;
		}
		if (NUMERIC_PASSENGER_FIELDS.has(key)) {
			const asNumber = Number(normalizedValue);
			return isNaN(asNumber) ? 0 : asNumber;
		}
		return normalizedValue;
	}

	private validateSlotValue(slot: ResolvedSlot, value: any): string {
		if (!slot || !slot.key || !slot.editable) {
			return '';
		}
		const key = slot.key;
		const normalized =
			typeof value === 'string'
				? value.trim()
				: value === undefined || value === null
				? ''
				: String(value).trim();
		if (NUMERIC_PASSENGER_FIELDS.has(key)) {
			if (normalized === '') {
				return '';
			}
			if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
				return `${slot.label} must be numeric.`;
			}
		}
		if ((key === 'pass_first_name' || key === 'pass_last_name') && normalized === '') {
			return `${slot.label} is required.`;
		}
		if (key === 'loc_str_nbr') {
			if (normalized === '') {
				return 'Street Number is required.';
			}
			if (!/^\d+$/.test(normalized) || Number(normalized) <= 0) {
				return 'Street Number must be a positive whole number.';
			}
		}
		if (
			(key === 'pelg_pass_allowed_trips_per_month' ||
				key === 'pelg_pass_allowed_miles_per_month' ||
				key === 'pelg_pass_allowed_units') &&
			normalized !== '' &&
			(!/^\d+$/.test(normalized) || Number(normalized) < 0)
		) {
			return `${slot.label} must be a non-negative whole number.`;
		}
		if (
			(key === 'pelg_pass_allowed_amount_per_month' ||
				key === 'pelg_pass_unit_amt') &&
			normalized !== '' &&
			(!/^\d+(\.\d+)?$/.test(normalized) || Number(normalized) < 0)
		) {
			return `${slot.label} must be a non-negative number.`;
		}
		if ((key === 'pelg_from_dt_tm' || key === 'pelg_to_dt_tm') && normalized !== '') {
			const asDate = this.parseToDate(normalized);
			if (!asDate) {
				return `${slot.label} must be a valid date/time.`;
			}
		}
		if (key === 'loc_ph_nbr') {
			const digits = normalized.replace(/\D/g, '');
			if (digits.length > 0 && digits.length !== 10) {
				return 'Phone Number must contain exactly 10 digits.';
			}
		}
		if (key === 'loc_zip_code' && normalized !== '' && !/^\d{5}$/.test(normalized)) {
			return 'ZIP Code must be exactly 5 digits.';
		}
		if (key === 'loc_state' && normalized !== '' && !/^[A-Za-z]{2}$/.test(normalized)) {
			return 'State must be exactly 2 letters.';
		}
		const maxLen = MAX_TEXT_LENGTH_BY_FIELD[key];
		if (maxLen === undefined) {
			return '';
		}
		const length = normalized.length;
		if (length > maxLen) {
			return `${slot.label} must be ${maxLen} characters or fewer.`;
		}
		return '';
	}

	private getEligibilityProgramValidationError(): string {
		const values = ELIGIBILITY_PROGRAM_FIELDS.map((field) =>
			this.resolveEffectiveNumericValue(field)
		);

		const hasTrips = values[0] > 0;
		const hasMiles = values[1] > 0;
		const hasAmount = values[2] > 0;
		const hasUnits = values[3] > 0 || values[4] > 0;
		const activeProgramCount = [hasTrips, hasMiles, hasAmount, hasUnits].filter(
			Boolean
		).length;

		if (activeProgramCount <= 1) {
			return '';
		}
		return 'Only one eligibility limit type can be set at a time (Trips, Miles, Amount, or Units).';
	}

	private resolveEffectiveNumericValue(field: string): number {
		const pending = this.pendingUpdates[field];
		const source = pending !== undefined ? pending : this.originalPassenger[field];
		const asNumber = Number(source);
		return isNaN(asNumber) ? 0 : asNumber;
	}

	hasSlotError(slot: ResolvedSlot): boolean {
		return !!(slot && slot.key && this.validationErrors[slot.key]);
	}

	getSlotError(slot: ResolvedSlot): string {
		return slot && slot.key ? this.validationErrors[slot.key] || '' : '';
	}

	private isSameValue(a: any, b: any): boolean {
		if (typeof a === 'number' || typeof b === 'number') {
			return Number(a) === Number(b);
		}
		return String(a == null ? '' : a).trim() === String(b == null ? '' : b).trim();
	}

	private buildPassengerFullName(): string {
		const first = this.formatDisplay(
			this.firstMatchingValue(['pass_first_name'])
		);
		const middle = this.formatDisplay(
			this.firstMatchingValue(['pass_middle_name'])
		);
		const last = this.formatDisplay(
			this.firstMatchingValue(['pass_last_name'])
		);
		const formatPart = (part: string) =>
			this.isHpdarBasicView ? this.toTitleCaseWords(part) : part;
		const fromParts = [first, middle, last]
			.filter((part) => !!part)
			.map(formatPart)
			.join(' ');
		if (fromParts) {
			return fromParts;
		}
		const fallback = this.formatDisplay(
			this.firstMatchingValue(['loc_passenger_name', 'loc_name', 'pass_id'])
		);
		return fallback || 'Passenger';
	}

	/** True if the API value should be shown (numbers like 0 / -1 count; blank strings do not). */
	private isPresent(v: any): boolean {
		if (v === undefined || v === null) {
			return false;
		}
		if (typeof v === 'string') {
			return v.trim() !== '';
		}
		return true;
	}

	/** Slotted fields: optional US date/datetime (UTC) or phone format per layout. */
	private formatFieldValue(v: any, dateFormat?: DetailDateFormat, format?: DetailFieldFormat): string {
		if (dateFormat) {
			const d = this.parseToDate(v);
			if (d) {
				return dateFormat === 'date' ? this.formatUsDateUtc(d) : this.formatUsDateTimeUtc(d);
			}
		}
		if (format === 'phone') {
			return this.formatPhone(v);
		}
		return this.formatDisplay(v);
	}

	/** Format a 10-digit phone string as (xxx) xxx-xxxx; falls back to raw value. */
	private formatPhone(v: any): string {
		const digits = String(v == null ? '' : v).replace(/\D/g, '');
		if (digits.length === 10) {
			return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
		}
		if (digits.length === 11 && digits[0] === '1') {
			return '(' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7);
		}
		return this.formatDisplay(v);
	}

	/** Dynamic “Additional” grid: infer date columns from key names. */
	private formatExtraFieldValue(key: string, v: any): string {
		const df = this.dateFormatForExtraKey(key);
		if (df) {
			const d = this.parseToDate(v);
			if (d) {
				return df === 'date' ? this.formatUsDateUtc(d) : this.formatUsDateTimeUtc(d);
			}
		}
		return this.formatDisplay(v);
	}

	private dateFormatForExtraKey(key: string): DetailDateFormat | null {
		if (/_dt_tm$/i.test(key)) {
			return 'datetime';
		}
		if (/(^|_)dob$/i.test(key) || /_birth_dt$/i.test(key) || /date_of_birth/i.test(key)) {
			return 'date';
		}
		return null;
	}

	private parseToDate(v: any): Date | null {
		if (v instanceof Date) {
			return isNaN(v.getTime()) ? null : v;
		}
		if (typeof v === 'string' && v.trim() !== '') {
			const d = new Date(v);
			return isNaN(d.getTime()) ? null : d;
		}
		if (typeof v === 'number' && isFinite(v)) {
			const d = new Date(v);
			return isNaN(d.getTime()) ? null : d;
		}
		return null;
	}

	private pad2(n: number): string {
		return n < 10 ? '0' + n : String(n);
	}

	/** MM/DD/YYYY in UTC (matches Zulu timestamps from API). */
	private formatUsDateUtc(d: Date): string {
		const m = d.getUTCMonth() + 1;
		const day = d.getUTCDate();
		const y = d.getUTCFullYear();
		return `${this.pad2(m)}/${this.pad2(day)}/${y}`;
	}

	/** MM/DD/YYYY h:mm AM/PM in UTC. */
	private formatUsDateTimeUtc(d: Date): string {
		const datePart = this.formatUsDateUtc(d);
		let h = d.getUTCHours();
		const min = d.getUTCMinutes();
		const ampm = h >= 12 ? 'PM' : 'AM';
		h = h % 12;
		if (h === 0) {
			h = 12;
		}
		return `${datePart} ${h}:${this.pad2(min)} ${ampm}`;
	}

	private formatDisplay(v: any): string {
		if (v === undefined || v === null) {
			return '';
		}
		if (typeof v === 'string' && v.trim() === '') {
			return '';
		}
		if (v instanceof Date) {
			const d = this.parseToDate(v);
			return d ? this.formatUsDateTimeUtc(d) : '';
		}
		if (typeof v === 'object') {
			try {
				return JSON.stringify(v);
			} catch (_e) {
				return String(v);
			}
		}
		if (typeof v === 'boolean') {
			return v ? 'Yes' : 'No';
		}
		return String(v).trim();
	}

	private coerceBool(v: any): boolean {
		if (v === true || v === 1) {
			return true;
		}
		const s = String(v)
			.trim()
			.toLowerCase();
		return s === 'y' || s === 'yes' || s === 'true' || s === '1';
	}

	private isActiveStatus(v: any): boolean {
		const s = String(v != null ? v : '')
			.trim()
			.toLowerCase();
		if (!s) {
			return false;
		}
		return (
			s.indexOf('active') !== -1 ||
			s === 'a' ||
			s === '1' ||
			s === 'yes' ||
			s === 'y'
		);
	}

	private labelForExtraKey(key: string): string {
		return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	}
}
